import { apiRequest } from './api'
import { TOKEN_KEY, USER_KEY } from '@/utils/constants'

// Authentication API service
export const authService = {
  // Login user
  login: async (credentials) => {
    const formData = new FormData()
    formData.append('username', credentials.email)
    formData.append('password', credentials.password)

    const response = await apiRequest.post('/auth/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    })

    const { access_token, token_type, user } = response.data

    // Store token and user data
    localStorage.setItem(TOKEN_KEY, access_token)
    localStorage.setItem(USER_KEY, JSON.stringify(user))

    return { token: access_token, user }
  },

  // Register new user
  register: async (userData) => {
    const response = await apiRequest.post('/auth/register', userData)
    return response.data
  },

  // Logout user
  logout: () => {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
  },

  // Get current user
  getCurrentUser: async () => {
    const response = await apiRequest.get('/auth/me')
    const user = response.data
    
    // Update stored user data
    localStorage.setItem(USER_KEY, JSON.stringify(user))
    
    return user
  },

  // Update user profile
  updateProfile: async (userData) => {
    const response = await apiRequest.put('/auth/profile', userData)
    const user = response.data

    // Update stored user data
    localStorage.setItem(USER_KEY, JSON.stringify(user))

    return user
  },

  // Reset password (for authenticated users changing their own password)
  resetPassword: async (formData) => {
    const response = await apiRequest.post('/auth/reset-password', formData)
    return response.data
  },

  // Change password
  changePassword: async (passwordData) => {
    const response = await apiRequest.post('/auth/change-password', passwordData)
    return response.data
  },

  // Request password reset
  requestPasswordReset: async (email) => {
    const response = await apiRequest.post('/auth/forgot-password', { email })
    return response.data
  },

  // Reset password with token (for forgot password flow)
  resetPasswordWithToken: async (token, newPassword) => {
    const response = await apiRequest.post('/auth/reset-password-token', {
      token,
      new_password: newPassword,
    })
    return response.data
  },

  // Verify email
  verifyEmail: async (token) => {
    const response = await apiRequest.post('/auth/verify-email', { token })
    return response.data
  },

  // Resend verification email
  resendVerification: async (email) => {
    const response = await apiRequest.post('/auth/resend-verification', { email })
    return response.data
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    const token = localStorage.getItem(TOKEN_KEY)
    return !!token
  },

  // Get stored user data
  getStoredUser: () => {
    const userData = localStorage.getItem(USER_KEY)
    return userData ? JSON.parse(userData) : null
  },

  // Get stored token
  getStoredToken: () => {
    return localStorage.getItem(TOKEN_KEY)
  },

  // Refresh access token
  refreshToken: async () => {
    const response = await apiRequest.post('/auth/refresh-token')
    const { access_token, user } = response.data

    // Update stored token and user data
    localStorage.setItem(TOKEN_KEY, access_token)
    if (user) {
      localStorage.setItem(USER_KEY, JSON.stringify(user))
    }

    return { token: access_token, user }
  },

  // Check if token is expired or near expiration
  isTokenExpired: (token) => {
    if (!token) return true

    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      const exp = payload.exp * 1000 // Convert to milliseconds
      const now = Date.now()

      return exp < now
    } catch (error) {
      console.error('Error parsing token:', error)
      return true
    }
  },

  // Check if token is near expiration (within 5 minutes)
  isTokenNearExpiration: (token) => {
    if (!token) return true

    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      const exp = payload.exp * 1000 // Convert to milliseconds
      const now = Date.now()
      const fiveMinutes = 5 * 60 * 1000 // 5 minutes in milliseconds

      return (exp - now) < fiveMinutes
    } catch (error) {
      console.error('Error parsing token:', error)
      return true
    }
  },

  // Check if user has permission
  hasPermission: (user, permission) => {
    if (!user) return false

    // Superusers have all permissions
    if (user.is_superuser) return true

    // Check explicit permissions
    if (!user.permissions) return false
    return user.permissions.includes(permission)
  },

  // Check if user has role
  hasRole: (user, role) => {
    if (!user) return false

    // Handle superuser as admin
    if (user.is_superuser && role === 'admin') {
      return true
    }

    // Check explicit role field
    if (user.role) {
      return user.role === role
    }

    // Fallback: if no role field but is_superuser is true, treat as admin
    if (user.is_superuser) {
      return role === 'admin'
    }

    return false
  },

  // Check if user can access brand
  canAccessBrand: (user, brandId) => {
    if (!user) return false

    // Admin/Superuser can access all brands
    if (user.is_superuser || user.role === 'admin') return true

    // Check brand access
    if (!user.brand_access || !Array.isArray(user.brand_access)) return false

    return user.brand_access.some(access =>
      access.brand_id === brandId && access.can_access
    )
  },

  // Check if user has specific permission for a brand
  hasBrandPermission: (user, brandId, permission) => {
    if (!user) return false

    // Admin/Superuser have all permissions
    if (user.is_superuser || user.role === 'admin') return true

    // Check brand access
    if (!user.brand_access || !Array.isArray(user.brand_access)) return false

    const brandAccess = user.brand_access.find(access => access.brand_id === brandId)
    if (!brandAccess) return false

    // Map permission names to brand access properties
    const permissionMap = {
      'view': 'can_view',
      'create': 'can_create',
      'edit': 'can_edit',
      'delete': 'can_delete',
      'manage_users': 'can_manage_users',
      'approve_requests': 'can_approve_requests'
    }

    const permissionKey = permissionMap[permission]
    return permissionKey ? brandAccess[permissionKey] : false
  },

  // Get user's accessible brand IDs
  getAccessibleBrandIds: (user) => {
    if (!user) return []

    // Admin/Superuser can access all brands (return empty array to indicate "all")
    if (user.is_superuser || user.role === 'admin') return []

    // Return specific brand IDs user has access to
    if (!user.brand_access || !Array.isArray(user.brand_access)) return []

    return user.brand_access
      .filter(access => access.can_access)
      .map(access => access.brand_id)
  },

  // Check if user can perform action on data input (based on brand)
  canManageDataInput: (user, dataInput, action = 'edit') => {
    if (!user || !dataInput) return false

    // Admin/Superuser can manage all data inputs
    if (user.is_superuser || user.role === 'admin') return true

    // Check brand permission
    return authService.hasBrandPermission(user, dataInput.brand_id, action)
  },
}
